import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Typography, Paper, TextField, IconButton, LinearProgress } from '@mui/material';
import { CloudUpload, Close, VideoLibrary } from '@mui/icons-material';
import CustomTextField from '../CustomTextField';
import MultiSelectDropDown from '../CustomComponents/MultiSelectDropDown';
import Loader from '../Loader';
import { headingBlue, red, mediumGrey, pureWhite, lightGrey } from '../../assets/colors';
import { buttonStyle, errorTextStyle } from '../../utils/commonCSS';
import HttpService from '../../backend/requestApis';
import { courses } from '../../backend/backendApis';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../CustomComponents/CustomComponentsSlice';
import { WORKerTypesList } from '../../utils/constants';
import AssessmentConfiguration from '../Training/Batch/AssessmentConfiguration';
import axios from 'axios';

const AddCourseDialog = (props) => {
  const { dialogStatus, updateDialogStatus } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Navigation steps: 1 = Course Info & Videos, 2 = Quiz Assessment Config
  const [step, setStep] = useState(1);

  // Basic Course info
  const [courseName, setCourseName] = useState('');
  const [courseNameError, setCourseNameError] = useState('');
  const [description, setDescription] = useState('');

  // Applicability list
  const [selectedApplicableFor, setSelectedApplicableFor] = useState([]);
  const [applicableError, setApplicableError] = useState('');

  // Videos
  const [files, setFiles] = useState([]);
  const [videoTitles, setVideoTitles] = useState([]);
  const [videoUploadError, setVideoUploadError] = useState('');
  const fileInputRef = useRef(null);

  const [isLoader, setLoader] = useState(false);
  const [apiError, setApiError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    setStep(1);
    setCourseName('');
    setCourseNameError('');
    setDescription('');
    setSelectedApplicableFor([]);
    setApplicableError('');
    setFiles([]);
    setVideoTitles([]);
    setVideoUploadError('');
    setApiError('');
    setIsUploading(false);
    setUploadProgress([]);
  }, [dialogStatus]);

  const handleFileChange = (e) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      let errorMsg = '';
      for (const file of selectedFiles) {
        if (!file.type.startsWith('video/')) {
          errorMsg = 'All uploaded files must be videos.';
          break;
        }
        // Validate individual file size (250 MB limit)
        if (file.size > 250 * 1024 * 1024) {
          errorMsg = 'Each video file must be under 250 MB.';
          break;
        }
      }

      if (errorMsg) {
        setVideoUploadError(errorMsg);
      } else {
        setFiles(prev => [...prev, ...selectedFiles]);
        setVideoTitles(prev => [
          ...prev,
          ...selectedFiles.map(file => file.name.split('.').slice(0, -1).join('.'))
        ]);
        setVideoUploadError('');
      }
    }
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, idx) => idx !== index));
    setVideoTitles(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleTitleChange = (index, value) => {
    setVideoTitles(prev => {
      const copy = [...prev];
      copy[index] = value;
      return copy;
    });
  };

  const validateStep1 = () => {
    let isValid = true;

    // Course Name check
    if (!courseName.trim()) {
      setCourseNameError('Course Name is required.');
      isValid = false;
    } else {
      setCourseNameError('');
    }

    // Video check
    if (files.length === 0) {
      setVideoUploadError('At least one video file is required.');
      isValid = false;
    } else {
      setVideoUploadError('');
    }

    // Worker categories check
    if (selectedApplicableFor.length === 0) {
      setApplicableError('Please select at least one applicable worker category.');
      isValid = false;
    } else {
      setApplicableError('');
    }

    return isValid;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleCreateCourse = async (assessmentData) => {
    if (!validateStep1()) return;

    setLoader(true);
    setIsUploading(true);
    setApiError('');
    setUploadProgress(files.map(() => 0));

    try {
      const uploadedVideos = [];

      // Step 1: Upload each video sequentially to DigitalOcean Spaces using presigned URLs
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const title = videoTitles[i] || file.name.split('.').slice(0, -1).join('.');

        // 1.1 Request presigned URL from backend
        const presignedOptions = {
          url: '/courses/presigned-url',
          method: 'GET',
          params: {
            fileName: file.name,
            contentType: file.type || 'video/mp4'
          },
          headers: {
            Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
          }
        };

        const presignedResp = await HttpService(presignedOptions);
        const { uploadUrl, fileKey } = presignedResp.data.data;

        // 1.2 Perform direct PUT upload to Spaces with progress tracking
        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type || 'video/mp4'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => {
              const copy = [...prev];
              copy[i] = progress;
              return copy;
            });
          }
        });

        uploadedVideos.push({
          title,
          fileUrl: fileKey
        });
      }

      // Step 2: Submit course details with the uploaded videos metadata as JSON
      const finalIsQuiz = !!(assessmentData && assessmentData.isAssessmentRequired);
      const appForValues = selectedApplicableFor.map(item => {
        const label = typeof item === 'object' ? item.name : item;
        if (label === 'Public') return 'public';
        const match = WORKerTypesList.find(t => t.label === label);
        return match ? match.value : label;
      });

      const payload = {
        courseName,
        description,
        isQuiz: finalIsQuiz,
        applicableFor: JSON.stringify(appForValues),
        videos: JSON.stringify(uploadedVideos)
      };

      if (finalIsQuiz && assessmentData) {
        payload.attemptsAllowed = assessmentData.attemptsAllowed || 1;
        const assessmentConfig = {
          easyQuestions: assessmentData.easyQuestions,
          mediumQuestions: assessmentData.mediumQuestions,
          hardQuestions: assessmentData.hardQuestions,
          qualificationPercentage: assessmentData.qualificationPercentage,
          time: assessmentData.time
        };
        payload.assessmentConfig = JSON.stringify(assessmentConfig);
      }

      // CRITICAL: Double nested headers for POST authentication
      const options = {
        url: courses,
        method: 'POST',
        payload: payload,
        headers: {
          headers: {
            Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '',
            'Content-Type': 'application/json'
          }
        }
      };

      await HttpService(options);
      setLoader(false);
      setIsUploading(false);
      dispatch(showSnackBar({ message: 'Course created successfully!', severity: 'success' }));
      updateDialogStatus(false, true); // Close and refresh table

    } catch (err) {
      setLoader(false);
      setIsUploading(false);
      console.error('Error creating course: ', err);
      if (err?.status === 403 || err?.response?.status === 403) {
        localStorage.clear();
        navigate('/Login');
      } else {
        setApiError(err?.response?.data?.message || 'Error creating course. Please try again.');
      }
    }
  };

  const titleStyle = {
    backgroundColor: headingBlue,
    color: 'white',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem 1.25rem'
  };

  return (
    <Dialog maxWidth="md" fullWidth open={dialogStatus} onClose={() => updateDialogStatus(false)}>
      <DialogTitle style={titleStyle}>
        <Typography style={{ fontWeight: 600, fontSize: '1rem' }}>Add New Course/ Quiz</Typography>
        <IconButton onClick={() => updateDialogStatus(false)} style={{ color: 'white', padding: 0 }} disabled={isUploading}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ padding: '1.5rem 2rem' }}>
        {isLoader && <Loader />}

        {/* Multi-step Header - Always Visible */}
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem', borderBottom: `2px solid ${lightGrey}`, width: '100%' }}>
          <Box sx={{ display: 'flex', gap: '3rem' }}>
            {['Course Info', 'Assessment'].map((label, index) => {
              const s = index + 1;
              const isActive = step === s;
              return (
                <Typography
                  key={s}
                  onClick={() => {
                    if (s === 1 || (s === 2 && validateStep1())) {
                      setStep(s);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    padding: '0.2rem 0.5rem 0.5rem 0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? headingBlue : 'gray',
                    borderBottom: isActive ? `2px solid ${headingBlue}` : '2px solid transparent',
                    marginBottom: '-2px',
                    transition: 'all 0.2s',
                    '&:hover': { color: headingBlue }
                  }}
                >
                  {label}
                </Typography>
              );
            })}
          </Box>
        </Box>

        {/* Step 1: Course Info, Videos */}
        {step === 1 && (
          <Grid container spacing={2}>
            {/* Section 1: Course Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: headingBlue, marginBottom: '0.5rem' }}>
                Course Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <CustomTextField
                placeHolder="Course Name *"
                value={courseName}
                setFunc={setCourseName}
                width="100%"
              />
              <Typography style={errorTextStyle}>{courseNameError}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <CustomTextField
                placeHolder="Course Description (Optional)"
                value={description}
                setFunc={setDescription}
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MultiSelectDropDown
                placeHolder="Applicable For *"
                list={[
                  { name: 'Public' },
                  ...WORKerTypesList.map(type => ({ name: type.label }))
                ]}
                value={selectedApplicableFor}
                setFunc={setSelectedApplicableFor}
                width="100%"
              />
              <Typography style={errorTextStyle}>{applicableError}</Typography>
            </Grid>

            {/* Section 2: Video Uploads */}
            <Grid item xs={12} style={{ marginTop: '0.5rem' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: headingBlue, marginBottom: '0.5rem' }}>
                Upload Videos (Max 250 MB per video)
              </Typography>
              <Box
                style={{
                  border: `2px dashed ${mediumGrey}`,
                  borderRadius: '8px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  backgroundColor: lightGrey,
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  opacity: isUploading ? 0.6 : 1
                }}
                onClick={() => !isUploading && fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  multiple
                  accept="video/*"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  disabled={isUploading}
                />
                <CloudUpload style={{ fontSize: '2.5rem', color: headingBlue, marginBottom: '0.5rem' }} />
                <Typography style={{ fontSize: '0.9rem', color: '#555' }}>
                  {isUploading ? 'Uploading videos directly to cloud Spaces...' : 'Click to browse and select video files'}
                </Typography>
              </Box>
              <Typography style={errorTextStyle}>{videoUploadError}</Typography>
            </Grid>

            {/* List of uploaded videos for Title customization */}
            {files.length > 0 && (
              <Grid item xs={12}>
                <Paper variant="outlined" style={{ padding: '1rem', backgroundColor: '#fafafa' }}>
                  <Typography variant="caption" style={{ fontWeight: 'bold', color: headingBlue, display: 'block', marginBottom: '0.5rem' }}>
                    Customize Video Titles:
                  </Typography>
                  <Grid container spacing={1.5}>
                    {files.map((file, idx) => (
                      <Grid item xs={12} sm={6} key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'stretch' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <VideoLibrary style={{ color: headingBlue, fontSize: '1.2rem' }} />
                          <TextField
                            size="small"
                            value={videoTitles[idx] || ''}
                            onChange={(e) => handleTitleChange(idx, e.target.value)}
                            label={`Title for Video ${idx + 1}`}
                            variant="outlined"
                            fullWidth
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                            disabled={isUploading}
                          />
                          {!isUploading && (
                            <IconButton size="small" style={{ color: red, padding: 4 }} onClick={() => handleRemoveFile(idx)}>
                              <Close fontSize="small" />
                            </IconButton>
                          )}
                        </div>
                        {isUploading && uploadProgress[idx] !== undefined && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, px: 0.5 }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={uploadProgress[idx]}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: '#e0e0e0',
                                  '& .MuiLinearProgress-bar': { backgroundColor: headingBlue }
                                }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: headingBlue, minWidth: '2.5rem', textAlign: 'right' }}>
                              {`${uploadProgress[idx]}%`}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            )}

            {apiError && (
              <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0 0 0' }}>
                <Typography style={{ ...errorTextStyle, fontSize: '0.85rem' }}>{apiError}</Typography>
              </Grid>
            )}

            <Grid item xs={12} style={{ padding: '1.5rem 0 0 0', display: 'flex', justifyContent: 'center' }}>
              <Button style={{ ...buttonStyle, backgroundColor: red, padding: '0.3rem 1.5rem' }} onClick={() => updateDialogStatus(false)} disabled={isUploading}>
                Cancel
              </Button>
              <Button style={{ ...buttonStyle, padding: '0.3rem 1.5rem', marginLeft: '1rem' }} onClick={handleNext} disabled={isUploading}>
                Next
              </Button>
            </Grid>
          </Grid>
        )}

        {/* Step 2: Assessment configuration using AssessmentConfiguration */}
        {step === 2 && (
          <Box sx={{ mt: 1 }}>
            <AssessmentConfiguration
              hideActions={true}
              onBack={() => setStep(1)}
              onSubmit={handleCreateCourse}
              submitText="Save"
              initialValues={{ isAssessmentRequired: false, attemptsAllowed: 1 }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddCourseDialog;
